import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useSupabaseUser } from '@/hooks/useSupabaseUser';
import { supabase } from '@/utils/supabase';
import { carregarMeuAcesso, registrarLogoutAdmin, type ExcecaoPermissao, type MfaOperador, type OperadorAdmin } from '@/utils/adminAuth';
import { temPermissao } from '@/utils/adminPermissoes';
import {
  CANAL_SESSAO,
  decidirEncerrarSessaoTemporaria,
  limparPreferenciaSessao,
} from '@/utils/sessaoConsole';

export type AdminAuthStatus =
  | 'carregando'
  | 'anonimo'
  | 'negado'
  | 'mfa_cadastro'
  | 'mfa_pendente'
  | 'senha_provisoria'
  | 'autorizado';

interface AdminAuthValor {
  status: AdminAuthStatus;
  operador: OperadorAdmin | null;
  permissoes: string[];
  /** Exceções individuais vigentes do operador (CONCEDER/NEGAR). */
  excecoes: ExcecaoPermissao[];
  /** Estado real do segundo fator (exigência do perfil + TOTP cadastrado). */
  mfa: MfaOperador;
  erro: string | null;
  /** Checa a permissão RECURSO:AÇÃO. */
  pode: (recurso: string, acao: string) => boolean;
  /** Checa o escopo efetivo de atuação (sem escopos = todos). */
  temEscopo: (escopo: string) => boolean;
  /** Somente o Super Administrador (SUPER_ADMIN) pode ver a Lixeira e restaurar/excluir de vez. */
  ehSuperAdmin: boolean;
  recarregar: () => void;
  sair: () => Promise<void>;
}

const MFA_VAZIO: MfaOperador = { exigido: false, configurado: false, motivo: null };

const VALOR_PADRAO: AdminAuthValor = {
  status: 'carregando',
  operador: null,
  permissoes: [],
  excecoes: [],
  mfa: MFA_VAZIO,
  erro: null,
  pode: () => false,
  temEscopo: () => false,
  ehSuperAdmin: false,
  recarregar: () => {},
  sair: async () => {},
};

const AdminAuthContext = createContext<AdminAuthValor | null>(null);

/**
 * Estado de autenticação do Console Administrativo PETBEA.
 * Usa a MESMA sessão Supabase do PETBEA — o vínculo de operador é resolvido no backend.
 */
export function AdminAuthProvider({ children }: { children: ReactNode }) {
  // Um provedor já existente é reutilizado (ex.: RotaProtegida envolve a rota e
  // a página também monta o AdminShell). Evita duas consultas de "me".
  const pai = useContext(AdminAuthContext);
  const { user, loading: authLoading } = useSupabaseUser();
  const [operador, setOperador] = useState<OperadorAdmin | null>(null);
  const [permissoes, setPermissoes] = useState<string[]>([]);
  const [excecoes, setExcecoes] = useState<ExcecaoPermissao[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [versao, setVersao] = useState(0);
  const [carregandoAcesso, setCarregandoAcesso] = useState(false);
  const [mfaPendente, setMfaPendente] = useState(false);
  const [mfaCadastro, setMfaCadastro] = useState(false);
  const [mfa, setMfa] = useState<MfaOperador>(MFA_VAZIO);

  // "Manter conectado" desligado => a sessão temporária termina ao fechar o navegador.
  // Consulta outras abas antes de encerrar, para não derrubar uma sessão ainda viva.
  useEffect(() => {
    if (pai) return;
    let ativo = true;
    let canal: BroadcastChannel | null = null;
    if (typeof BroadcastChannel !== 'undefined') {
      canal = new BroadcastChannel(CANAL_SESSAO);
      canal.onmessage = (evento) => {
        if (evento.data === 'petbea-ping') canal?.postMessage('petbea-pong');
      };
    }
    decidirEncerrarSessaoTemporaria()
      .then((encerrar) => {
        if (ativo && encerrar) void supabase.auth.signOut();
      })
      .catch(() => {
        /* sem storage disponível — segue com a sessão persistente */
      });
    return () => {
      ativo = false;
      canal?.close();
    };
  }, [pai]);

  useEffect(() => {
    if (pai) return;
    let ativo = true;

    if (authLoading) {
      setCarregandoAcesso(true);
      return () => {
        ativo = false;
      };
    }

    if (!user?.id) {
      setOperador(null);
      setPermissoes([]);
      setExcecoes([]);
      setErro(null);
      setMfaPendente(false);
      setMfaCadastro(false);
      setMfa(MFA_VAZIO);
      setCarregandoAcesso(false);
      return () => {
        ativo = false;
      };
    }

    setCarregandoAcesso(true);
    (async () => {
      try {
        const r = await carregarMeuAcesso();
        const mfaServidor: MfaOperador = r.operador?.mfa ?? {
          exigido: r.operador?.mfa_obrigatorio === true,
          configurado: r.operador?.mfa_habilitado === true,
          motivo: null,
        };
        let pendente = false;
        let cadastro = false;
        if (mfaServidor.exigido && !mfaServidor.configurado) {
          // Exigido pelo perfil, mas sem TOTP cadastrado: o operador é levado
          // ao cadastro do segundo fator antes de usar o Console.
          cadastro = true;
        } else if (mfaServidor.configurado) {
          const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
          pendente = !!data && data.currentLevel !== 'aal2';
        }
        if (!ativo) return;
        setOperador(r.operador);
        setPermissoes(r.permissoes ?? []);
        setExcecoes(r.excecoes ?? []);
        setMfa(mfaServidor);
        setMfaCadastro(cadastro);
        setMfaPendente(pendente);
        setErro(null);
      } catch (e) {
        if (!ativo) return;
        setOperador(null);
        setPermissoes([]);
        setExcecoes([]);
        setMfaPendente(false);
        setMfaCadastro(false);
        setMfa(MFA_VAZIO);
        setErro(e instanceof Error ? e.message : 'Não foi possível verificar seu acesso.');
      } finally {
        if (ativo) setCarregandoAcesso(false);
      }
    })();

    return () => {
      ativo = false;
    };
  }, [user?.id, authLoading, versao, pai]);

  // Qualquer função de borda que responda 403 mfa_requerido leva a sessão de
  // volta ao desafio do código (a tela de login trata o passo).
  useEffect(() => {
    if (pai) return;
    const aoExigirMfa = () => {
      setMfaCadastro(false);
      setMfaPendente(true);
    };
    window.addEventListener('petbea:mfa_requerido', aoExigirMfa);
    return () => window.removeEventListener('petbea:mfa_requerido', aoExigirMfa);
  }, [pai]);

  const recarregar = useCallback(() => setVersao((v) => v + 1), []);

  const sair = useCallback(async () => {
    await registrarLogoutAdmin();
    limparPreferenciaSessao();
    await supabase.auth.signOut();
    setOperador(null);
    setPermissoes([]);
    setExcecoes([]);
    setMfa(MFA_VAZIO);
    setMfaCadastro(false);
    setMfaPendente(false);
  }, []);

  const valor = useMemo<AdminAuthValor>(() => {
    let status: AdminAuthStatus = 'carregando';
    if (!authLoading && !carregandoAcesso) {
      if (!user?.id) status = 'anonimo';
      else if (!operador) status = 'negado';
      else if (operador.senha_provisoria) status = 'senha_provisoria';
      else if (mfaCadastro) status = 'mfa_cadastro';
      else if (mfaPendente) status = 'mfa_pendente';
      else status = 'autorizado';
    }

    const escoposEfetivos = operador?.escopos ?? [];
    const semRestricaoEscopo = operador?.escopo_modo === 'TODOS' || escoposEfetivos.length === 0;

    return {
      status,
      operador,
      permissoes,
      excecoes,
      mfa,
      erro,
      pode: (recurso: string, acao: string) => temPermissao(permissoes, recurso, acao),
      temEscopo: (escopo: string) => semRestricaoEscopo || escoposEfetivos.includes(escopo),
      ehSuperAdmin: operador?.papel_codigo === 'SUPER_ADMIN',
      recarregar,
      sair,
    };
  }, [
    authLoading,
    carregandoAcesso,
    user?.id,
    operador,
    permissoes,
    excecoes,
    mfa,
    erro,
    mfaPendente,
    mfaCadastro,
    recarregar,
    sair,
  ]);

  if (pai) return <>{children}</>;

  return <AdminAuthContext.Provider value={valor}>{children}</AdminAuthContext.Provider>;
}

/** Lê o estado de autenticação administrativa compartilhado. */
export function useAdminAuth(): AdminAuthValor {
  return useContext(AdminAuthContext) ?? VALOR_PADRAO;
}